import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, MapPin, MessageSquare, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { HoardexaLoader } from "@/components/HoardexaLoader";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeMoney(show, value) {
  return show ? inr(value) : "Hidden";
}

export function PublicProposal() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, status: "" });
  const [password, setPassword] = useState("");
  const [response, setResponse] = useState({ response_type: "accepted", client_name: "", client_email: "", client_mobile: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const load = async (pw = password) => {
    setState((p) => ({ ...p, loading: true }));
    try {
      const { data } = await api.get(`/public/proposals/${token}`, { headers: pw ? { "X-Proposal-Password": pw } : {} });
      setState({ loading: false, ...data });
    } catch (err) {
      const data = err.response?.data;
      setState({ loading: false, status: data?.status || "error", message: data?.message || formatApiError(data?.detail) });
    }
  };

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const proposal = state.proposal;
  const link = state.link || {};
  const showPrices = link.show_prices !== false;
  const cities = useMemo(() => [...new Set(asArray(proposal?.items).map((item) => item.city).filter(Boolean))], [proposal]);
  const types = useMemo(() => [...new Set(asArray(proposal?.items).map((item) => item.type).filter(Boolean))], [proposal]);

  async function download(kind) {
    try {
      const res = await api.get(`/public/proposals/${token}/download/${kind}`, { responseType: "blob", headers: password ? { "X-Proposal-Password": password } : {} });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Hoardexa_Proposal.${kind === "excel" ? "xlsx" : kind === "ppt" ? "pptx" : kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download not allowed or link unavailable.");
    }
  }

  async function submitResponse(type) {
    try {
      await api.post(`/public/proposals/${token}/response`, { ...response, response_type: type });
      setSubmitted(true);
      toast.success("Response submitted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  if (state.loading) return <PublicShell><HoardexaLoader label="Loading proposal" /></PublicShell>;

  if (state.status === "password_required") {
    return (
      <PublicShell>
        <div className="mx-auto max-w-md py-24">
          <div className="rounded-lg bg-white p-6 shadow-lg shadow-slate-200/60 border border-slate-200">
            <h1 className="text-xl font-bold text-slate-950">Password required</h1>
            <p className="mt-2 text-sm text-slate-500">Enter the proposal password shared by the sender.</p>
            <Input type="password" className="mt-4" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button className="mt-4 w-full bg-blue-600 transition hover:bg-blue-700 hover:-translate-y-px" onClick={() => load(password)}>Open proposal</Button>
          </div>
        </div>
      </PublicShell>
    );
  }

  if (state.status !== "active") {
    return (
      <PublicShell>
        <div className="mx-auto max-w-xl py-24 text-center">
          <XCircle className="mx-auto text-slate-400" size={44} />
          <h1 className="mt-4 text-2xl font-bold text-slate-950">{state.message || "Proposal unavailable"}</h1>
          <p className="mt-2 text-sm text-slate-500">Please contact the sender for an updated proposal.</p>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="mx-auto max-w-6xl px-5 py-14">
          <div className="max-w-3xl">
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-red-200">Hoardexa Media Proposal</div>
            <h1 className="mt-3 text-4xl font-bold tracking-tight">{proposal.details.campaign_name}</h1>
            <p className="mt-3 text-lg text-slate-300">{proposal.details.client_name} {proposal.details.brand_name ? `| ${proposal.details.brand_name}` : ""}</p>
            <p className="mt-2 text-sm text-slate-400">Valid until {new Date(link.expires_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              {link.allow_pdf_download && <Button onClick={() => download("pdf")} className="bg-white text-slate-950 transition hover:bg-slate-100 hover:-translate-y-px"><Download size={16} /> Download PDF</Button>}
              {link.allow_excel_download && <Button onClick={() => download("excel")} variant="outline" className="border-white/30 text-white transition hover:bg-white/10 hover:-translate-y-px">Excel</Button>}
              {link.allow_ppt_download && <Button onClick={() => download("ppt")} variant="outline" className="border-white/30 text-white transition hover:bg-white/10 hover:-translate-y-px">PowerPoint</Button>}
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-5 py-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <SummaryCard label="Total Sites" value={proposal.summary.total_selected} />
          <SummaryCard label="Total Cost" value={safeMoney(showPrices, proposal.summary.total_cost)} />
          <SummaryCard label="Duration" value={`${proposal.summary.duration_months} mo`} />
          <SummaryCard label="Cities" value={cities.length} />
          <SummaryCard label="Media Types" value={types.length} />
        </div>

        <section>
          <h2 className="text-2xl font-bold text-slate-950">Selected Media</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {asArray(proposal.items).map((item) => (
              <article key={item.id || item.hoarding_id} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition hover:border-blue-200 hover:shadow-md">
                {item.photo_url ? <img src={item.photo_url} alt="" className="h-52 w-full object-cover" /> : <div className="h-52 bg-slate-100" />}
                <div className="p-4 space-y-3">
                  <div>
                    <div className="text-xs font-mono text-slate-400">{item.hoarding_id}</div>
                    <h3 className="font-semibold text-slate-950">{item.name}</h3>
                    <p className="text-sm text-slate-500">{item.area}, {item.city}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <Spec label="Type" value={item.type} />
                    <Spec label="Size" value={item.size} />
                    <Spec label="Rate" value={safeMoney(showPrices, item.monthly_price)} />
                    <Spec label="Total" value={safeMoney(showPrices, item.total_cost)} />
                    <Spec label="Status" value={item.occupancy_status} />
                  </div>
                  {item.map_url && <a href={item.map_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 transition hover:text-red-800 hover:underline"><MapPin size={15} /> View location</a>}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Breakup title="Cities Covered" rows={proposal.summary.city_wise} showPrices={showPrices} />
          <Breakup title="Media Types" rows={proposal.summary.type_wise} showPrices={showPrices} />
          <div className="rounded-lg bg-white border border-slate-200 p-5">
            <h2 className="font-bold text-slate-950">Terms / Notes</h2>
            <p className="mt-3 text-sm text-slate-600 whitespace-pre-wrap">{proposal.details.notes || "Rates and availability are subject to confirmation at booking."}</p>
          </div>
        </section>

        {link.allow_client_response && (
          <section className="rounded-lg bg-white border border-slate-200 p-5">
            <div className="flex items-center gap-2">
              <MessageSquare size={18} className="text-blue-600" />
              <h2 className="font-bold text-slate-950">Respond to Proposal</h2>
            </div>
            {submitted ? (
              <div className="mt-4 rounded-lg bg-emerald-50 p-4 text-emerald-800 inline-flex items-center gap-2"><CheckCircle2 size={18} /> Response submitted successfully.</div>
            ) : (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input placeholder="Your name" value={response.client_name} onChange={(e) => setResponse({ ...response, client_name: e.target.value })} />
                <Input placeholder="Email" value={response.client_email} onChange={(e) => setResponse({ ...response, client_email: e.target.value })} />
                <Input placeholder="Mobile" value={response.client_mobile} onChange={(e) => setResponse({ ...response, client_mobile: e.target.value })} />
                <Textarea className="md:col-span-2" rows={3} placeholder="Comment or requested changes" value={response.message} onChange={(e) => setResponse({ ...response, message: e.target.value })} />
                <div className="md:col-span-2 flex flex-wrap gap-2">
                  <Button onClick={() => submitResponse("accepted")} className="bg-emerald-600 transition hover:bg-emerald-700 hover:-translate-y-px">Accept Proposal</Button>
                  <Button onClick={() => submitResponse("changes_requested")} className="bg-amber-600 transition hover:bg-amber-700 hover:-translate-y-px">Request Changes</Button>
                  <Button onClick={() => submitResponse("rejected")} variant="outline" className="transition hover:-translate-y-px">Reject</Button>
                </div>
              </div>
            )}
          </section>
        )}
      </main>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return <div className="min-h-screen bg-slate-50">{children}</div>;
}

function SummaryCard({ label, value }) {
  return <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-200"><div className="text-xs text-slate-500">{label}</div><div className="mt-1 text-xl font-bold text-slate-950">{value}</div></div>;
}

function Spec({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div><div className="font-semibold text-slate-800">{value || "-"}</div></div>;
}

function Breakup({ title, rows = [], showPrices }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-5">
      <h2 className="font-bold text-slate-950">{title}</h2>
      <div className="mt-3 space-y-2">
        {asArray(rows).map((row) => (
          <div key={row.key} className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium text-slate-700">{row.key}</span>
            <span className="text-slate-500">{row.count} sites {showPrices ? `| ${inr(row.cost)}` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
